import { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { Tooltip } from "../Tooltip";
import {
  colors,
  Modal,
  Button,
  InfoModal,
  QuestionsModal,
  CustomButton,
} from "../../reusable-components";
import config from "../../config";
import { toTitleCase } from "../../utils";
import { useAsyncFn } from "../../hooks/use-async-fn";

import FAQIcon from "../../assets/faq.svg";
import SupportIcon from "../../assets/support.svg";
import UserIcon from "../../assets/user.svg";
import saptharushiLogo from "../../resources/logo_cut_no_bg.png";

import UserRoleIconSVGComponent from "../svg-component/userRoleIcon";
import OrganizationIconSVGComponent from "../svg-component/organizationIcon";
import UserRoleTagIconSVGComponent from "../svg-component/UserRoleTagIcon";
import UserRoleFederationSVGComponent from "../svg-component/userRoleFederation";
import LogoutIcon from "../../assets/logout.svg";

import { ColorContext } from "../../contexts/color-context/ColorContext";
import styles from "./header.module.css";
import { useFederation } from "../FederationSelection/FederationContext";

interface StyledHeaderProps {
  primaryColor?: string;
}
const StyledHeader = styled.div<StyledHeaderProps>`
  font-size: var(--font-sm);
  color: ${colors.shadowGray};
  background: white;
  padding: var(--space-sm) var(--space-sm);
  overflow: hidden;
  position: fixed;
  top: 0;
  width: 100%;
  height: var(--footer-height);
  display: flex;
  z-index: var(--z-modal);
`;

const StyledIQuestionModal = styled(QuestionsModal)`
  z-index: var(--z-overlay);
  @keyframes stylediquestionmodal-fade-in {
    0% {
      opacity: 0.5;
      transform: translate(50%, calc(-1 * var(--space-sm)));
    }
    100% {
      opacity: 1;
    }
  }
  .modal-container {
    position: absolute;
    right: var(--space-md);
    top: calc(var(--space-4xl) + var(--space-sm));
    margin: var(--space-0);
    height: 80%;
    width: 40%;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-md);
    animation-name: stylediquestionmodal-fade-in;
    animation-duration: 0.6s;
  }
  .modal-container::-webkit-scrollbar {
    width: var(--space-sm);
  }
  .modal-container::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: var(--radius-xs);
  }
  .modal-container::-webkit-scrollbar-track {
    background-color: #f0f0f0;
  }
  .modal-content {
    margin: var(--space-0);
    padding: var(--space-0);
    min-width: calc(var(--space-5xl) * 2.9);
  }
  .user-name {
    color: ${colors.navyBlue};
    font-size: var(--font-md);
    font-weight: 700;
    margin-bottom: var(--space-sm);
  }
  .user-info {
    display: flex;
    justify-content: space-between;
    color: ${colors.primaryGray};
    & > span {
      font-weight: 700;
    }
    & > span:first-of-type {
      margin-right: var(--space-sm);
      font-weight: 400;
    }
  }
  .user-modal-buttons {
    & > * {
      margin-top: var(--space-sm);
    }
    & > *:first-of-type {
      margin-top: var(--space-md);
    }
  }
`;

const StyledInfoModal = styled(InfoModal)`
  z-index: var(--z-overlay);
  @keyframes styledinfomodal-fade-in {
    0% {
      opacity: 0.5;
      transform: translate(50%, calc(-1 * var(--space-sm)));
    }
    100% {
      opacity: 1;
    }
  }
  .modal-container {
    position: absolute;
    right: var(--space-md);
    top: calc(var(--space-4xl) + var(--space-sm));
    margin: var(--space-0);
    border-radius: var(--radius-xl);
    background: #fff;
    box-shadow: var(--shadow-md);
    animation-name: styledinfomodal-fade-in;
    animation-duration: 0.6s;
  }
  .modal-content {
    margin: var(--space-0);
    padding: var(--space-0);
    min-width: calc(var(--space-5xl) * 2.9);
  }
  .user-name {
    color: ${colors.navyBlue};
    font-size: var(--font-md);
    font-weight: 700;
    margin-bottom: var(--space-sm);
  }
  .user-info {
    display: flex;
    justify-content: space-between;
    color: ${colors.primaryGray};
    & > span {
      font-weight: 700;
    }
    & > span:first-of-type {
      margin-right: var(--space-sm);
      font-weight: 400;
    }
  }
  .user-modal-buttons {
    & > * {
      margin-top: var(--space-sm);
    }
    & > *:first-of-type {
      margin-top: var(--space-md);
    }
  }
`;

const StyledModal = styled(Modal)`
  @keyframes styledmodal-fade-in {
    0% {
      opacity: 0.5;
      transform: translate(50%, calc(-1 * var(--space-sm)));
    }
    100% {
      opacity: 1;
    }
  }
  .modal-container {
    position: absolute;
    right: var(--space-md);
    top: calc(var(--space-4xl) + var(--space-sm));
    margin: var(--space-0);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-md);
    animation-name: styledmodal-fade-in;
    animation-duration: 0.6s;
    width: calc(var(--space-5xl) * 2.6);
    min-height: calc(var(--space-5xl) * 2);
    max-height: calc(var(--space-5xl) * 3.125);
    overflow-y: auto;
  }
  .modal-container::-webkit-scrollbar {
    width: var(--space-sm);
  }
  .modal-container::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: var(--radius-xs);
  }
  .modal-container::-webkit-scrollbar-track {
    background-color: #f0f0f0;
  }
  .modal-content {
    margin: var(--space-0);
    padding: var(--space-0);
    min-width: calc(var(--space-5xl) * 2.9);
  }
  .user-name {
    color: ${colors.navyBlue};
    font-size: var(--font-md);
    font-weight: 700;
    margin-bottom: var(--space-sm);
  }
  .user-info {
    display: flex;
    justify-content: space-between;
    color: ${colors.primaryGray};
    & > span {
      font-weight: 700;
    }
    & > span:first-of-type {
      margin-right: var(--space-sm);
      font-weight: 400;
    }
  }
  .user-modal-buttons {
    & > * {
      margin-top: var(--space-sm);
    }
    & > *:first-of-type {
      margin-top: var(--space-md);
    }
  }
`;

export const StyledPaginationButton = styled(Button)`
  position: relative;
  overflow: hidden;
  opacity: 1;
  height: var(--space-4xl);
  margin: var(--space-sm) 0;
  font-size: var(--font-md);
  font-weight: 700;
  text-transform: unset;
  cursor: pointer;
  align-self: center;
  font-family: ${({ actionElementFamily }) => actionElementFamily};
  color: ${({ primaryColor }) => primaryColor};
`;

const Header = ({ authContext }) => {
  const {
    clearAuth,
    user,
    getorganizationById,
    appMetadata,
    getApplication,
    getConfig,
    // getPermissionsbyrole,
    getFAQ,
    getPermissionsbyrole,
  } = authContext;
  const { platformLogo } = useFederation();
  const { given_name, family_name, email } = user || {};

  const colorContext = useContext(ColorContext);

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--Primary-Color-Shade-1")
    .trim();
  document.documentElement.style.setProperty("--primary-color", primaryColor);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInfoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const [isQueriesDropdownOpen, setQueriesDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);

  const [secondaryColorCode, setSecondaryColorCode] = useState("");
  const [, setElementColorCode] = useState("");
  const [, setButtonShape] = useState("");

  const [staticElementColor, setStaticElementColor] = useState("");
  const [, setStaticElementFamily] = useState("");
  const [actionElementColor, setActionElementColor] = useState("");
  const [, setActionElementFamily] = useState("");

  const [, setDataFontType] = useState("");
  const [loading, setLoading] = useState(true);
  const [showOrgAppMismatch, setShowOrgAppMismatch] = useState(false);
  const [mismatchInfo, setMismatchInfo] = useState<{ orgId: string; organizationName: string | null; applicationId: string; applicationName: string | null } | null>(null);

  const [, setPlatformFont] = useState("");
  const [, setPlatfomFontColor] = useState("");
  const [, setDataFontColor] = useState("");

  const [applicationName, setApplicationName] = useState("");
  const [applicationLogo, setApplicationLogo] = useState("");
  const [version, setVersion] = useState("");
  const [date, setDate] = useState("");
  const [releaseLink, setReleaseLink] = useState("");

  const getApplicationFn = useAsyncFn({
    promiseFn: getApplication,
  });

  let tenantObj = {};
  tenantObj = appMetadata && Object.values(appMetadata);
  const organizationId = tenantObj[0]?.organization;

  const getFqsConfigFn = useAsyncFn({
    promiseFn: getConfig,
    organizationId: organizationId,
  });

  var applicationData = getApplicationFn.data && getApplicationFn.data;
  const configData = getFqsConfigFn.data;

  var authRole = tenantObj[0]?.roles[0];
  localStorage.setItem("authRole", authRole);
  const getPermissionsbyroleFn = useAsyncFn({
    promiseFn: getPermissionsbyrole,
    userRole: authRole,
  });

  const requiredPages = [
    "createfederation",
    "campaignplanning",
    "query",
    "provenancetrail",
    "activationhistory",
    "activationconnectors",
    "datamanager",
    "management_dashboard",
    "organization_dashboard",
  ];

  const apiPermissions = getPermissionsbyroleFn?.data?.rbacGroupName || [];

  const finalPermissions = requiredPages.map((page) => {
    const match = apiPermissions.find((perm) => perm.startsWith(`${page}_`));
    return match || `${page}_no_access`;
  });

  const permissionsObj = {};

  const roles = getPermissionsbyroleFn?.data?.roles || [];
  const metadataRoles = tenantObj[0]?.roles || [];

  const isSuperUserReadOnly =
    metadataRoles.includes(config.superUserRole) &&
    metadataRoles.includes("read_only");

  const isAdminUser =
    roles.includes(config.superUserRole) ||
    roles.includes(config.applicationAdminRole) ||
    roles.includes("organization_admin");

  if (isSuperUserReadOnly) {
    Object.assign(permissionsObj, {
      createfederation: "full",
      campaignplanning: "no",
      query: "no",
      provenancetrail: "read",
      activationhistory: "no",
      activationconnectors: "no",
      datamanager: "no",
      management_dashboard: "yes",
      organization_dashboard: "no",
    });
    localStorage.setItem("isSuperUserReadOnly", "true");
  } else if (isAdminUser) {
    const isPlatformOrAppAdminUser =
      roles.includes(config.superUserRole) ||
      roles.includes(config.applicationAdminRole);
    Object.assign(permissionsObj, {
      createfederation: "full",
      campaignplanning: "read",
      query: "full",
      provenancetrail: "read",
      activationhistory: "full",
      activationconnectors: "full",
      datamanager: "full",
      management_dashboard: isPlatformOrAppAdminUser ? "read" : "no",
      organization_dashboard: "read",
    });
    localStorage.removeItem("isSuperUserReadOnly");
  } else {
    const dashboardPages = ["management_dashboard", "organization_dashboard"];
    finalPermissions.forEach((perm) => {
      const dashPage = dashboardPages.find((dp) => perm.startsWith(dp + "_"));
      if (dashPage) {
        // e.g. "management_dashboard_read_access" → "read"
        const suffix = perm.slice(dashPage.length + 1);
        const accessType = suffix.replace(/_access$/, "");
        permissionsObj[dashPage] = accessType;
      } else {
        const [page, accessType] = perm.split("_");
        permissionsObj[page] = accessType;
      }
    });
    localStorage.removeItem("isSuperUserReadOnly");
  }
  localStorage.setItem("userPermissions", JSON.stringify(permissionsObj));
  localStorage.setItem("userRole", roles);
  useEffect(() => {
    localStorage.setItem(
      "configScope",
      applicationData?.setting?.toLowerCase(),
    );
    // ONAM: the tab icon stays the ONAM mark (public/index.html), not the application logo
  }, [applicationData]);

  const getFAQFn = useAsyncFn({
    promiseFn: getFAQ,
  });

  const FAQResults = getFAQFn.data && getFAQFn.data.faq;

  useEffect(() => {
    if (configData && configData.message) {
      setVersion(configData.message?.version);
      setDate(configData.message?.release_date);
      setReleaseLink(configData.message?.release_notes_link);
    }
  }, [configData]);

  useEffect(() => {
    const applicationData = getApplicationFn.data && getApplicationFn.data;
    if (
      getApplicationFn.data &&
      applicationData &&
      applicationData.color_scheme
    ) {
      setApplicationName(applicationData.name);
      setApplicationLogo(applicationData.logo);

      setSecondaryColorCode(applicationData.color_scheme.secondary_color);
      setElementColorCode(applicationData.color_scheme.element_color);

      setPlatformFont(
        applicationData.apperance.component_settings.platform_title.font_type,
      );
      setPlatfomFontColor(
        applicationData.apperance.component_settings.platform_title.font_color,
      );
      setDataFontColor(
        applicationData.apperance.component_settings.data.font_color,
      );

      setButtonShape(applicationData.apperance.button_shape);
      setStaticElementColor(
        applicationData.apperance.component_settings.labels.font_color,
      );
      setActionElementColor(
        applicationData.apperance.component_settings.action_button_label
          .font_color,
      );
      setStaticElementFamily(
        applicationData.apperance.component_settings.labels.font_type,
      );
      setActionElementFamily(
        applicationData.apperance.component_settings.action_button_label
          .font_type,
      );

      setDataFontType(
        applicationData.apperance.component_settings.data.font_type,
      );
      setLoading(false);
    }
  }, [getApplicationFn.data]);

  useEffect(() => {
    const err = getApplicationFn.error as Error | undefined;
    if (!err?.message?.startsWith("ORG_APP_MISMATCH:")) return;
    try {
      const info = JSON.parse(err.message.slice("ORG_APP_MISMATCH:".length));
      setMismatchInfo(info);
      setShowOrgAppMismatch(true);
    } catch {}
  }, [getApplicationFn.error]);

  const { secondaryfedcolor } = colorContext;

  const viewHomeSettings = () => {
    setQueriesDropdownOpen(!isQueriesDropdownOpen);
  };

  const previousQuestion = () => {
    setPage(page - 1);
  };

  const nextQuestion = () => {
    setPage(page + 1);
  };

  localStorage.setItem("organizationkey", organizationId);
  const getorganizationByIdFn = useAsyncFn({
    deferFn: getorganizationById,
  });
  const { run: runGetOrganizationById } = getorganizationByIdFn;

  useEffect(() => {
    if (organizationId) {
      runGetOrganizationById({ organizationID: organizationId });
    }
  }, [organizationId, runGetOrganizationById]);

  const sessionFederationName = localStorage.getItem("federationname") || "";

  const organizationData =
    getorganizationByIdFn.data && getorganizationByIdFn.data;
  const organizationName = organizationData?.name || "";
  const organizationType = organizationData?.organization_type || "";
  const atomiserData = organizationData?.atomiser?.atomizer_enabled || "";
  const organizationTag = organizationData?.tag || "";

  const userRole = localStorage.getItem("authRole") || "";
  const userRoleName = userRole
    ? toTitleCase(userRole.replace(/[^a-zA-Z 0-9]+/g, " "))
    : "";

  if (organizationType)
    localStorage.setItem("organizationtype", organizationType);
  if (organizationName)
    localStorage.setItem("organizationName", organizationName);
  if (atomiserData) localStorage.setItem("organizationAtomiser", atomiserData);

  return (
    <>
      {showOrgAppMismatch && (
        <OrgAppMismatchOverlay>
          <OrgAppMismatchCard>
            <OrgAppMismatchHeader>
              <OrgAppMismatchHeaderIcon>⚠</OrgAppMismatchHeaderIcon>
              Access Configuration Error
            </OrgAppMismatchHeader>
            <OrgAppMismatchBody>
              <OrgAppMismatchMessage>
                Your organization is not linked to this application. Please contact your administrator to resolve this access issue.
              </OrgAppMismatchMessage>
              <OrgAppMismatchInfoTable>
                <OrgAppMismatchInfoRow>
                  <OrgAppMismatchDetailLabel>Organization</OrgAppMismatchDetailLabel>
                  <OrgAppMismatchDetailName>{mismatchInfo?.organizationName || mismatchInfo?.orgId || "—"}</OrgAppMismatchDetailName>
                </OrgAppMismatchInfoRow>
                <OrgAppMismatchDivider />
                <OrgAppMismatchInfoRow>
                  <OrgAppMismatchDetailLabel>Application</OrgAppMismatchDetailLabel>
                  <OrgAppMismatchDetailName>{mismatchInfo?.applicationName || mismatchInfo?.applicationId || "—"}</OrgAppMismatchDetailName>
                </OrgAppMismatchInfoRow>
              </OrgAppMismatchInfoTable>
            </OrgAppMismatchBody>
            <OrgAppMismatchFooter>
              <OrgAppMismatchLogoutBtn onClick={clearAuth}>Logout</OrgAppMismatchLogoutBtn>
            </OrgAppMismatchFooter>
          </OrgAppMismatchCard>
        </OrgAppMismatchOverlay>
      )}
      {!loading && (
        <StyledHeader>
          <div className={styles.headerRoot}>
            <div className={styles.headerLeft}>
              <a href="/" className={styles.logoLink}>
                <img
                  src={platformLogo || applicationData?.logo}
                  alt=""
                  className={styles.platformLogo}
                />
              </a>

              <span className={styles.applicationName}>{applicationName}</span>

              <div className={styles.verticalLine} />

              {sessionFederationName && (
                <span className={styles.fedName}>{sessionFederationName}</span>
              )}
            </div>

            <div className={styles.headerRight}>
              <ul className={styles.navList}>
                <li className={styles.navItem}>
                  {isInfoDropdownOpen && (
                    <StyledInfoModal
                      onClose={() => {
                        setInfoDropdownOpen(false);
                      }}
                    >
                      <div className={styles.infoModalRoot}>
                        <div className={styles.infoHeader}>
                          <div className={styles.infoHeaderLeft}>
                            <img
                              src={applicationLogo || saptharushiLogo}
                              alt="Application Logo"
                              className={styles.infoAppLogo}
                            />
                          </div>

                          <div className={styles.infoHeaderRight}>
                            <span className={styles.infoAppName}>
                              {applicationName}
                            </span>
                          </div>
                        </div>

                        <div className={styles.infoContent}>
                          <div className={styles.infoRow}>
                            <div className={styles.infoLabelWrapper}>
                              <span className={styles.infoLabel}>
                                Product Version
                              </span>
                            </div>
                            <div className={styles.infoValueWrapper}>
                              <span className={styles.infoValue}>
                                {version}
                              </span>
                            </div>
                          </div>

                          <div className={styles.infoRow}>
                            <div className={styles.infoLabelWrapper}>
                              <span className={styles.infoLabel}>
                                Release Date
                              </span>
                            </div>
                            <div className={styles.infoValueWrapper}>
                              <span className={styles.infoValueCapitalized}>
                                {date}
                              </span>
                            </div>
                          </div>

                          <div className={styles.infoRow}>
                            <div className={styles.infoLabelWrapper}>
                              <span className={styles.infoLabel}>
                                Support email
                              </span>
                            </div>
                            <div className={styles.infoValueWithMargin}>
                              <a
                                href="mailto:support@saptharushi.com"
                                className={styles.infoEmailLink}
                              >
                                support@saptharushi.com
                              </a>
                            </div>
                          </div>

                          <div className={styles.infoRowWithTopMargin}>
                            <div className={styles.releaseDocWrapper}>
                              <a
                                href={releaseLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.releaseDocLink}
                              >
                                <span className={styles.releaseDocText}>
                                  Release Document
                                </span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </StyledInfoModal>
                  )}

                  <Tooltip text="Release Notes & Support" placement="bottom">
                    <div className={styles.svgContainer}>
                      <img
                        src={SupportIcon}
                        alt="support"
                        onClick={() => setInfoDropdownOpen(!isInfoDropdownOpen)}
                      />
                    </div>
                  </Tooltip>
                </li>

                {isQueriesDropdownOpen && (
                  <StyledIQuestionModal
                    onClose={() => {
                      setQueriesDropdownOpen(false);
                    }}
                  >
                    <div className={styles.faqModalRoot}>
                      <div className={styles.faqHeader}>
                        <span className={styles.faqTitle}>FAQs</span>
                      </div>

                      {page >= 1 && page <= 7 && (
                        <div
                          className={
                            page === 1
                              ? styles.faqContentWithTopPadding
                              : styles.faqContent
                          }
                        >
                          {FAQResults?.slice(
                            (page - 1) * 10,
                            page === 7 ? 66 : page * 10,
                          )?.map((data, index) => (
                            <div key={index} className={styles.faqItem}>
                              <div className={styles.faqQuestion}>
                                {data.question}
                              </div>
                              <div className={styles.faqAnswer}>
                                {data.answer}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className={styles.faqPagination}>
                        {page > 1 ? (
                          <span
                            className={styles.faqPrevEnabled}
                            onClick={previousQuestion}
                          >
                            Previous
                          </span>
                        ) : (
                          <span className={styles.faqPrevDisabled}>
                            Previous
                          </span>
                        )}

                        {page < 7 ? (
                          <span
                            className={styles.faqNextEnabled}
                            onClick={nextQuestion}
                          >
                            Next
                          </span>
                        ) : (
                          <span className={styles.faqNextDisabled}>Next</span>
                        )}
                      </div>
                    </div>
                  </StyledIQuestionModal>
                )}

                <li className={styles.navItem}>
                  <Tooltip text="Help & FAQs" placement="bottom">
                    <div className={styles.svgContainer}>
                      <img src={FAQIcon} alt="faq" onClick={viewHomeSettings} />
                    </div>
                  </Tooltip>
                </li>

                <li className={styles.navItem}>
                  {isDropdownOpen && (
                    <StyledModal
                      onClose={() => {
                        setIsDropdownOpen(false);
                      }}
                    >
                      <div className={styles.userModalRoot}>
                        <div className={styles.userModalHeader}>
                          <span className={styles.userName}>
                            {given_name}{" "}
                            {family_name !== "-" ? family_name : ""}
                          </span>
                          <span className={styles.userEmail}>{email}</span>
                        </div>

                        <hr className={styles.userModalDivider} />

                        <div className={styles.userModalContent}>
                          {organizationName && (
                            <div className={styles.userInfoRow}>
                              <div className={styles.userInfoLeft}>
                                <OrganizationIconSVGComponent
                                  primaryColor={staticElementColor}
                                />
                                <span className={styles.userInfoLabel}>
                                  Organization
                                </span>
                              </div>
                              <div className={styles.userInfoRight}>
                                <span className={styles.userInfoValue}>
                                  {organizationName}
                                </span>
                              </div>
                            </div>
                          )}

                          {organizationTag && (
                            <div className={styles.userInfoRowNoMargin}>
                              <div className={styles.userInfoLeft}>
                                <UserRoleTagIconSVGComponent
                                  primaryColor={staticElementColor}
                                />
                                <span className={styles.userInfoLabel}>
                                  Organization Role
                                </span>
                              </div>
                              <div className={styles.userInfoRight}>
                                <span className={styles.userInfoValue}>
                                  {organizationTag}
                                </span>
                              </div>
                            </div>
                          )}

                          {userRoleName && (
                            <div className={styles.userInfoRowNoMargin}>
                              <div className={styles.userInfoLeft}>
                                <UserRoleIconSVGComponent
                                  primaryColor={staticElementColor}
                                  secondaryColor={
                                    secondaryfedcolor || secondaryColorCode
                                  }
                                />
                                <span className={styles.userInfoLabel}>
                                  User Role
                                </span>
                              </div>
                              <div className={styles.userInfoRight}>
                                <span className={styles.userInfoValue}>
                                  {userRoleName}
                                </span>
                              </div>
                            </div>
                          )}

                          {sessionFederationName && (
                            <div className={styles.userInfoRowNoMargin}>
                              <div className={styles.userInfoLeft}>
                                <UserRoleFederationSVGComponent
                                  primaryColor={staticElementColor}
                                />
                                <span className={styles.userInfoLabel}>
                                  Federation
                                </span>
                              </div>
                              <div className={styles.userInfoRight}>
                                <span className={styles.userInfoValue}>
                                  {sessionFederationName}
                                </span>
                              </div>
                            </div>
                          )}

                          {isAdminUser && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginBottom: "0.75rem",
                                marginRight: "35px",
                              }}
                            >
                              <a
                                href={config.adminAppUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: primaryColor,
                                  textDecoration: "underline",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Go to Admin App
                              </a>
                            </div>
                          )}
                          <div className={styles.logoutWrapper}>
                            <CustomButton
                              className={styles.logoutButton}
                              backgroundColor={primaryColor}
                              textColor={actionElementColor}
                              text="LOGOUT"
                              after={
                                <div className={styles.logoutIconWrapper}>
                                  <img
                                    src={LogoutIcon}
                                    height={18}
                                    width={18}
                                    alt="logout"
                                  />
                                </div>
                              }
                              onClick={() => {
                                setIsDropdownOpen(false);
                                clearAuth();
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </StyledModal>
                  )}

                  <Tooltip text="My Account" placement="bottom-end">
                    <div className={styles.svgContainer}>
                      <img
                        src={UserIcon}
                        alt="user"
                        onClick={() => {
                          setIsDropdownOpen(!isDropdownOpen);
                        }}
                      />
                    </div>
                  </Tooltip>
                </li>
              </ul>
            </div>
          </div>
        </StyledHeader>
      )}
    </>
  );
};

export default Header;

const OrgAppMismatchOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const OrgAppMismatchCard = styled.div`
  background: white;
  border-radius: 12px;
  width: 540px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  @keyframes mismatch-slide-in {
    from {
      opacity: 0;
      transform: translateY(-16px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  animation: mismatch-slide-in 0.22s ease-out;
`;

const OrgAppMismatchHeader = styled.div`
  background: #dc2626;
  color: white;
  padding: 16px 24px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-align: center;
  font-family: "Montserrat", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const OrgAppMismatchHeaderIcon = styled.span`
  font-size: 16px;
`;

const OrgAppMismatchBody = styled.div`
  padding: 28px 32px 20px;
`;

const OrgAppMismatchMessage = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.65;
  margin: 0 0 24px;
  text-align: center;
`;

const OrgAppMismatchInfoTable = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const OrgAppMismatchInfoRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  gap: 16px;
`;

const OrgAppMismatchDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 0;
`;

const OrgAppMismatchDetailLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #9ca3af;
  width: 96px;
  flex-shrink: 0;
`;

const OrgAppMismatchDetailName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const OrgAppMismatchFooter = styled.div`
  padding: 4px 32px 28px;
  display: flex;
  justify-content: center;
`;

const OrgAppMismatchLogoutBtn = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 11px 48px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.4px;
  transition: background 0.15s, transform 0.1s;

  &:hover {
    background: #b91c1c;
  }

  &:active {
    transform: scale(0.98);
  }
`;
